import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { stat } from 'fs';
import { RootState } from 'store';

type itemSizes = {
  activity: any;
  videos: any;
};

export type AppState = {
  sidebarOpen: boolean;
  secondaryViewOpen: boolean;
  secondaryViewData: any;
  keyEvent: string;
  secondaryViewWidth: number;
  sidebarWidth: number;
  sidebarAbsolute: boolean;
  notifications: any[];
  scrollPostions: any;
  itemSizes: Record<string, itemSizes>;
  lastTab: any;
  external: boolean;
  activePlaylistId: any;
};

const initialState: AppState = {
  sidebarOpen: true,
  secondaryViewOpen: false,
  secondaryViewData: {},
  keyEvent: '',
  secondaryViewWidth: 450,
  sidebarWidth: 260,
  sidebarAbsolute: false,
  notifications: [],
  scrollPostions: {},
  itemSizes: {},
  lastTab: {},
  external: false,
  activePlaylistId: {}
};

const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    handleSidebarOpen(state, action) {
      state.sidebarOpen = action.payload;
    },
    handleSecondaryViewOpen(state, action) {
      state.secondaryViewOpen = action.payload.open;
      if (action.payload.data) {
        state.secondaryViewData = action.payload.data;
      }
    },
    handleKeyEvent(state, action) {
      state.keyEvent = action.payload;
    },
    handleSecondaryViewWidth(state, action) {
      state.secondaryViewWidth = action.payload;
    },
    handleSidebarWidth(state, action) {
      state.sidebarWidth = action.payload;
    },
    handleSidebarAbsolute(state, action) {
      state.sidebarAbsolute = action.payload;
    },

    addNotification(state, action) {
      state.notifications.push(action.payload);
    },
    removeAllNotification(state) {
      state.notifications = [];
    },
    handleScrollPostion(state, action) {
      if (!state.scrollPostions[action.payload.key]) {
        state.scrollPostions[action.payload.key] = null;
      }
      state.scrollPostions[action.payload.key] = action.payload.data;
    },
    clearActivePlayList(state, action) {
      if (action.payload.channelId) {
        state.activePlaylistId[action.payload.channelId] = null;
      }
    },
    handleActivePlaylist(state, action) {
      if (action.payload.channelId) {
        const value = action.payload.data;
        state.activePlaylistId[action.payload.channelId] = value;
      }
    },
    handleExternal(state, action) {
      state.external = action.payload;
    },
    handleAddLastTab(state, action) {
      const { channelId, tab } = action.payload;
      state.lastTab[channelId] = tab;
    },
    handleActivityItemSize(
      state,
      action: PayloadAction<{
        tab: 'activity' | 'videos';
        itemSizes: any;
        channelId: string;
      }>
    ) {
      const { tab, itemSizes, channelId } = action.payload;

      // Ensure the channel object exists in state
      if (!state.itemSizes[channelId]) {
        state.itemSizes[channelId] = { activity: null, videos: null };
      }
      // Update the corresponding tab's item size
      if (tab in state.itemSizes[channelId]) {
        state.itemSizes[channelId][tab] = itemSizes;
      }
    },
    clearItemSize(state, action: PayloadAction<{ channelId: string; tab: 'activity' | 'videos' }>) {
      const { channelId, tab } = action.payload;
      if (state.itemSizes[channelId]) {
        state.itemSizes[channelId][tab] = null;
      }
    }
  }
});

export const {
  handleSidebarOpen,
  handleSecondaryViewOpen,
  handleKeyEvent,
  handleSecondaryViewWidth,
  handleSidebarWidth,
  handleSidebarAbsolute,
  addNotification,
  removeAllNotification,
  handleScrollPostion,
  handleExternal,
  handleActivePlaylist,
  clearActivePlayList,
  handleAddLastTab,
  handleActivityItemSize,
  clearItemSize
} = appSlice.actions;

export const getChannelItemSize = (
  state: AppState,
  channelId: string,
  tab: 'activity' | 'videos'
): Record<string, number> | null => {
  const channel = state.itemSizes[channelId];
  return channel ? (channel[tab] as Record<string, number>) : null;
};

export const sidebarOpenState = (state: RootState): boolean => state.app.sidebarOpen;
export const scrollPostionsState = (state: RootState): any => state.app.scrollPostions;
export const appReducer = appSlice.reducer;
